// Gráfico SVG da evolução do % custo de mercadoria vs meta.
// Largura medida (viewBox = px reais → sem distorção) e altura fixa.

import { useMemo, useState } from 'react'
import type { CostOfGoodsEntry } from '../../types'
import { computeRow, monthLabel } from '../../hooks/useCostOfGoods'
import { useContainerWidth } from '../../hooks/useContainerWidth'

interface Props {
  entries: CostOfGoodsEntry[] // ordenadas asc por (year, month)
}

const HEIGHT = 280
const PAD_L = 44   // espaço para labels do eixo Y
const PAD_R = 16
const PAD_T = 24
const PAD_B = 36   // espaço para labels do eixo X

const STATUS_FILL = {
  ok:      '#10b981', // emerald-500
  warning: '#facc15', // yellow-400
  danger:  '#ef4444', // red-500
  empty:   '#6b7280', // gray-500
} as const

function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

export default function CostOfGoodsChart({ entries }: Props) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const [ref, W] = useContainerWidth<HTMLDivElement>()

  const data = useMemo(() => {
    return entries.map((e) => {
      const r = computeRow(e)
      return {
        entry: e,
        label: monthLabel(e.period_year, e.period_month),
        pct: r.costPct,
        costReal: r.costReal,
        status: r.status,
        target: e.target_pct,
      }
    })
  }, [entries])

  // Domínio Y dinâmico: mínimo 0, máximo = max(maior pct + 10, 80)
  const maxPct = Math.max(80, ...data.map(d => d.pct ?? 0)) + 10
  const yMax = Math.ceil(maxPct / 10) * 10
  const yTicks = [0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax]

  const innerW = Math.max(W - PAD_L - PAD_R, 0)
  const innerH = HEIGHT - PAD_T - PAD_B

  const xOf = (i: number) =>
    data.length === 1 ? PAD_L + innerW / 2 : PAD_L + (i / Math.max(data.length - 1, 1)) * innerW
  const yOf = (pct: number) => PAD_T + innerH - (pct / yMax) * innerH

  const refTarget = data[data.length - 1]?.target ?? 55
  const yTarget = yOf(refTarget)

  // Afina os rótulos do eixo X p/ não encavalar: passo em função do espaço por
  // ponto. Ancorado no ÚLTIMO mês (o mais recente sempre aparece).
  const perPoint = data.length > 1 ? innerW / (data.length - 1) : innerW
  const xLabelStep = Math.max(1, Math.ceil(40 / Math.max(perPoint, 1)))
  const lastIdx = data.length - 1

  const validPoints = data
    .map((d, i) => ({ i, pct: d.pct }))
    .filter(p => p.pct != null) as Array<{ i: number; pct: number }>
  const polylinePoints = validPoints.map(p => `${xOf(p.i)},${yOf(p.pct)}`).join(' ')

  return (
    <div className="bg-surface border border-line rounded-xl p-4 sm:p-5 flex flex-col">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-ink">Evolução do % custo de mercadoria</h3>
        <div className="flex gap-4 text-xs text-ink-muted">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5 bg-sky-500" /> Custo %
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-0.5 border-t border-dashed border-red-400" /> Meta {refTarget}%
          </span>
        </div>
      </div>
      <p className="text-xs text-ink-faint mt-0.5 mb-2">% do custo real sobre o faturamento, mês a mês</p>

      <div ref={ref} className="relative w-full" style={{ height: HEIGHT }}>
        {data.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-ink-faint text-center px-4">
            Sem dados — adicione um período para visualizar o gráfico.
          </div>
        ) : W > 0 && (
          <svg viewBox={`0 0 ${W} ${HEIGHT}`} className="w-full block" role="img">
            {/* Grid horizontal + labels Y */}
            {yTicks.map((t, idx) => (
              <g key={idx}>
                <line x1={PAD_L} x2={W - PAD_R} y1={yOf(t)} y2={yOf(t)} className="stroke-line" strokeWidth="1" />
                <text x={PAD_L - 8} y={yOf(t) + 4} className="fill-ink-muted" fontSize="11" textAnchor="end">{t.toFixed(0)}%</text>
              </g>
            ))}

            {/* Linha da meta (tracejada vermelha) */}
            <line x1={PAD_L} x2={W - PAD_R} y1={yTarget} y2={yTarget} stroke="#f87171" strokeWidth="1.5" strokeDasharray="6 4" />

            {/* Polyline conectando os pontos válidos */}
            {validPoints.length >= 2 && (
              <polyline points={polylinePoints} fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinejoin="round" />
            )}

            {/* Pontos coloridos por status + área de hover */}
            {data.map((d, i) => {
              if (d.pct == null) return null
              const cx = xOf(i)
              const cy = yOf(d.pct)
              const fill = STATUS_FILL[d.status]
              const isHover = hoverIdx === i
              return (
                <g key={i}>
                  <rect
                    x={cx - Math.max(innerW / Math.max(data.length, 1) / 2, 12)} y={PAD_T}
                    width={Math.max(innerW / Math.max(data.length, 1), 24)} height={innerH}
                    fill="transparent"
                    onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}
                  />
                  <circle cx={cx} cy={cy} r={isHover ? 6 : 4} fill={fill} stroke="currentColor" strokeWidth="1.5" className="pointer-events-none transition-all text-paper" />
                </g>
              )
            })}

            {/* Labels eixo X (afinados p/ não encavalar) */}
            {data.map((d, i) => {
              if ((lastIdx - i) % xLabelStep !== 0) return null
              return (
                <text key={`x-${i}`} x={xOf(i)} y={HEIGHT - PAD_B + 18} className="fill-ink-muted" fontSize="10" textAnchor="middle">{d.label}</text>
              )
            })}
          </svg>
        )}

        {/* Tooltip */}
        {hoverIdx !== null && data[hoverIdx] && W > 0 && (
          <div
            className="absolute bg-surface border border-line rounded-lg px-3 py-2 text-xs pointer-events-none shadow-lg z-10"
            style={{ left: `${(xOf(hoverIdx) / W) * 100}%`, top: 0, transform: 'translateX(-50%)' }}
          >
            <div className="font-semibold text-ink">{data[hoverIdx].label}</div>
            <div className="text-ink-muted mt-1 space-y-0.5">
              <div className="whitespace-nowrap">Custo real: <span className="text-ink">{brl(data[hoverIdx].costReal)}</span></div>
              <div className="whitespace-nowrap">Faturamento: <span className="text-ink">{brl(data[hoverIdx].entry.revenue_gross)}</span></div>
              <div>Custo %: <span className="text-ink font-medium">{data[hoverIdx].pct != null ? data[hoverIdx].pct!.toFixed(1) + '%' : '—'}</span></div>
              <div>Status: <span style={{ color: STATUS_FILL[data[hoverIdx].status] }}>
                {data[hoverIdx].status === 'ok' ? 'Dentro da meta'
                 : data[hoverIdx].status === 'warning' ? 'Atenção'
                 : data[hoverIdx].status === 'danger' ? 'Acima da meta'
                 : 'Sem dados'}
              </span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
